import { Injectable, inject } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { map, of, pipe, switchMap, tap, timer } from 'rxjs';
import { UserDetails } from '../models/user.model';
import { LoginService } from '../shared/services/login-service';
import { LoginMethod } from '../features/login/auth.types';
import { Router } from '@angular/router';
import { STORAGE_KEYS } from '../shared/storage-keys';
import { environment } from 'src/environment';


export interface AuthState {
    readonly loggedUserDetails?: UserDetails;
    readonly token?: string;
    readonly errorKey?: string;
    readonly expireDate?: number;
    readonly loadingLogin?: boolean;
    readonly otpSent?: boolean;
    readonly sendingOtp?: boolean;
    readonly otpError?: string | null;
    readonly otpRemainingTime?: number | null;
}

@Injectable({ providedIn: 'root' })
export class AuthStore extends ComponentStore<AuthState> {

    ExpirationMinutes = environment.ExpirationMinutes ?? 20;
    #loginService = inject(LoginService);
    #router = inject(Router);

    #fromLastTimeMinutesInterval = environment.formLastTimeMinutesInterval ?? 20;
    #minuteBeforeExpired = 5;

    private otpExpirationTimer: any = null;
    readonly OTP_EXPIRY_MINUTES = 5;

    constructor() {
        super({
            token: localStorage.getItem(STORAGE_KEYS.userToken) ?? undefined,
        });
        this.init();
    }

    readonly loggedUserDetails$ = this.select(({ loggedUserDetails }) => loggedUserDetails);
    readonly token$ = this.select(({ token }) => token);
    readonly errorKey$ = this.select(({ errorKey }) => errorKey);
    readonly loadingLogin$ = this.select(({ loadingLogin }) => loadingLogin);
    readonly otpSent$ = this.select(({ otpSent }) => otpSent);
    readonly sendingOtp$ = this.select(({ sendingOtp }) => sendingOtp);
    readonly otpError$ = this.select(({ otpError }) => otpError);
    readonly otpRemainingTime$ = this.select(({ otpRemainingTime }) => otpRemainingTime);

    // Server failure strings that have their own message under login.errors. Anything not listed
    // here falls back to the generic ErrorOccurred.
    errorsKeys = ["UserDetailsNotFound", "PasswordNotCorrect", "NoPermission", "OtpInvalid", "ErrorOccurred"];

    init() {
        this.token$
            .pipe(
                switchMap((token) => {
                    if (token) {
                        const expiredTokenTime = new Date(Number(localStorage.getItem(STORAGE_KEYS.expiredTokenTime)));
                        const currentTime = new Date();
                        const secondsUntilExpiration = (expiredTokenTime.getTime() - currentTime.getTime()) / 1000;

                        if (secondsUntilExpiration <= 5) {
                            console.log("Token is expired or too close to expiration. Not refreshing.");
                            //check i am not in login page
                            // this.#router.navigate(['/login']);
                            this.navigateToLogin();
                            return of(undefined);// מחזיר Observable ריק כדי לא לבצע רענון
                        }
                        const millisecondsToDelay = (secondsUntilExpiration - (this.#minuteBeforeExpired * 60)) * 1000;
                        console.log("login active refresh tokem delay : ", millisecondsToDelay, " in minutes", millisecondsToDelay / (1000 * 60))
                        return timer(millisecondsToDelay).pipe(map(o => token));
                    } else {
                        return of(token);
                    }
                })).subscribe((token?: string) => {
                    if (token == null) {
                        return;
                    }
                    const lastDateSendReq = localStorage.getItem(STORAGE_KEYS.lastRequest);
                    if (lastDateSendReq) {
                        const lastDateAsDate = new Date(lastDateSendReq);
                        const lastDateAddMinutes = new Date(lastDateAsDate.setMinutes(lastDateAsDate.getMinutes() + this.#fromLastTimeMinutesInterval))
                        var isActive = lastDateAddMinutes > new Date();
                        if (isActive) {
                            console.log("login active refresh in ", new Date());
                            this.refreshToken();
                        }
                        else {
                            console.log("login navigate to login because time over of 20 miniutes");
                            this.navigateToLogin();
                        }
                    } else {
                        this.navigateToLogin();
                    }
                });
    }

    readonly login = this.effect<{ request: object, redirectTo: string, loginMethod?: LoginMethod }>(
        pipe(
            tap(() => this.resetError()),
            tap(() => this.patchState({ loadingLogin: true })),
            switchMap((props) =>
                this.#loginService.login(props.request, props.loginMethod).pipe(
                    tapResponse(
                        (respons) => {
                            var experationDateTime = (new Date().getTime() + 1000 * 60 * this.ExpirationMinutes);
                            localStorage.setItem(STORAGE_KEYS.userToken, respons.token);
                            localStorage.setItem(STORAGE_KEYS.expiredTokenTime, experationDateTime.toString());
                            console.log("login enter expire", experationDateTime);
                            this.patchState({ token: respons.token, expireDate: experationDateTime });

                            if (respons?.token != null) {
                                this.#router.navigateByUrl(props.redirectTo).catch(() => {
                                    if (props.redirectTo !== "/") {
                                        this.#router.navigateByUrl("/main");
                                    }
                                });
                            }
                            else {
                                this.patchState({ errorKey: "ErrorOccurred" });
                            }
                            this.patchState({ loadingLogin: false });
                        },
                        (err: any) => {
                            // A mistyped code is the one failure the user can fix on the spot, and
                            // the code they were sent is still valid - the server only marks an OTP
                            // used once it verifies. So the field stays open for another attempt.
                            const isWrongCode = err?.error === "OtpInvalid";

                            if (err?.status === 429) {
                                // Rate limiter or per-account OTP lockout on the server. The body is
                                // deliberately empty there, so the status is all we have to go on -
                                // "try again later" is also all we want to tell the caller.
                                this.patchState({ token: undefined, expireDate: undefined, errorKey: "tooManyRequests" });
                            } else if (this.errorsKeys.includes(err.error)) {
                                this.patchState({ token: undefined, expireDate: undefined, errorKey: err.error });
                            } else {
                                console.log("error occurred in login", err);
                                this.patchState({ token: undefined, expireDate: undefined, errorKey: "ErrorOccurred" });
                            }

                            // Every other failure - locked out, throttled, unknown user, unexpected
                            // error - has nothing to retype, so it collapses back to the send-a-code
                            // button. Clearing it for a wrong code too was what forced a fresh SMS
                            // per typo, and those resends count against both the send rate limit and
                            // the per-account lockout. The expiry timer still closes the field on its
                            // own once the code actually expires (handleOtpExpiration).
                            if (!isWrongCode) {
                                this.patchState({ otpSent: false });
                            }
                            this.patchState({ loadingLogin: false });
                        }
                    )
                )
            )
        )
    );

    readonly refreshToken = () => {
        if (environment.auth == "idp") {
            // this.refreshTokenIDP(); -- מצריך פתרון 
            this.refreshTokenJWT();
        } else {
            this.refreshTokenJWT();
        }
    }

    readonly refreshTokenJWT = this.effect<void>(
        pipe(
            tap(() => this.resetError()),
            switchMap(() =>
                this.#loginService.refreshToken().pipe(
                    tapResponse(
                        (response) => {
                            const experationDateTime =
                                new Date().getTime() + 1000 * 60 * this.ExpirationMinutes;

                            if (response?.token != null) {
                                localStorage.setItem(STORAGE_KEYS.userToken, response.token);
                                localStorage.setItem(
                                    STORAGE_KEYS.expiredTokenTime,
                                    experationDateTime.toString()
                                );

                                this.patchState({
                                    token: response.token,
                                    expireDate: experationDateTime
                                });
                            } else {
                                this.clearAuthData();
                                this.patchState({ errorKey: "ErrorOccurred" });
                                this.navigateToLogin();
                            }
                        },
                        (err: any) => {
                            if (err?.status === 401) {
                                console.log("refresh token failed: user is inactive or unauthorized");

                                this.clearAuthData();

                                this.patchState({
                                    token: undefined,
                                    expireDate: undefined,
                                    errorKey: "Unauthorized"
                                });

                                this.navigateToLogin();
                                return;
                            }

                            if (this.errorsKeys.includes(err?.error)) {
                                this.patchState({
                                    token: undefined,
                                    expireDate: undefined,
                                    errorKey: err.error
                                });
                            } else {
                                console.log("error occurred in refresh token", err);

                                this.patchState({
                                    token: undefined,
                                    expireDate: undefined,
                                    errorKey: "ErrorOccurred"
                                });
                            }
                        }
                    )
                )
            )
        )
    );
    private clearAuthData(): void {
        localStorage.removeItem(STORAGE_KEYS.userToken);
        localStorage.removeItem(STORAGE_KEYS.expiredTokenTime);
        localStorage.removeItem(STORAGE_KEYS.lastRequest);

        this.patchState({
            token: undefined,
            expireDate: undefined
        });
    }
    readonly refreshTokenIDP = this.effect<void>(
        pipe(
            tap(() => this.resetError()),
            switchMap((props) =>
                this.#loginService.refreshTokenIDP().pipe(
                    tapResponse(
                        (respons) => {
                        },
                        (err: any) => {
                        }
                    )
                )
            )
        )
    );

    readonly sendOtp = this.effect<string>(
        pipe(
            tap(() => this.patchState({ sendingOtp: true, otpError: null })),
            tap(() => this.clearOtpTimer()),
            tap(() => this.patchState({ otpSent: false, otpRemainingTime: null })),
            switchMap((idValue) =>
                this.#loginService.sendOtp({ tz: idValue }).pipe(
                    tapResponse(
                        (response) => {
                            if (response.success) {
                                this.patchState({
                                    otpSent: true,
                                    otpError: null,
                                    sendingOtp: false
                                });
                                this.startOtpTimer();
                            } else {
                                this.patchState({
                                    otpError: 'login.errors.otpSendFailed',
                                    sendingOtp: false
                                });
                            }
                        },
                        (error: any) => {
                            console.error('Error sending OTP:', error);
                            let errorMessage = 'login.errors.ErrorOccurred';

                            // Checked before the body: the server answers 429 with an empty body
                            // (rate limiter rejection, or the OTP lockout), so the `if (error.error)`
                            // branch below is never entered for it and its own 429 case - which sits
                            // inside that branch - could never be reached.
                            if (error.status === 429) {
                                errorMessage = 'login.errors.tooManyRequests';
                            } else if (error.error) {
                                if (typeof error.error === 'string') {
                                    errorMessage = error.error;
                                } else if (error.error.message) {
                                    errorMessage = error.error.message;
                                } else if (error.error.error) {
                                    errorMessage = error.error.error;
                                } else {
                                    switch (error.status) {
                                        case 400:
                                            errorMessage = 'login.errors.invalidId';
                                            break;
                                        case 404:
                                            errorMessage = 'login.errors.UserDetailsNotFound';
                                            break;
                                        case 429:
                                            errorMessage = 'login.errors.tooManyRequests';
                                            break;
                                        default:
                                            errorMessage = 'login.errors.otpSendFailed';
                                    }
                                }
                            }

                            this.patchState({
                                otpError: errorMessage,
                                sendingOtp: false
                            });
                        }
                    )
                )
            )
        )
    );

    private clearOtpTimer() {
        if (this.otpExpirationTimer) {
            clearInterval(this.otpExpirationTimer);
            this.otpExpirationTimer = null;
        }
    }

    private startOtpTimer() {
        this.clearOtpTimer();
        let remainingSeconds = this.OTP_EXPIRY_MINUTES * 60;
        this.patchState({ otpRemainingTime: remainingSeconds });

        this.otpExpirationTimer = setInterval(() => {
            remainingSeconds--;
            this.patchState({ otpRemainingTime: remainingSeconds });
            if (remainingSeconds <= 0) {
                this.handleOtpExpiration();
            }
        }, 1000);
    }

    private handleOtpExpiration() {
        this.clearOtpTimer();
        this.patchState({
            otpSent: false,
            otpRemainingTime: null,
            otpError: 'login.otpExpired'
        });
    }

    getFormattedRemainingTime(): string {
        const remaining = this.get(s => s.otpRemainingTime);
        if (remaining === null || remaining === undefined) return '';
        const minutes = Math.floor(remaining / 60);
        const seconds = remaining % 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }

    readonly resetOtpOnIdChange = this.updater((state) => {
        this.clearOtpTimer();
        return {
            ...state,
            otpSent: false,
            otpError: null,
            otpRemainingTime: null
        };
    });

    readonly resetOtpState = this.updater((state) => ({
        ...state,
        otpSent: false,
        otpError: null,
        otpRemainingTime: null
    }));

    readonly resetError = this.updater((state) => ({
        ...state,
        errorKey: undefined
    }));

    readonly logOut = this.updater((state) => {
        localStorage.removeItem(STORAGE_KEYS.userToken);
        localStorage.removeItem(STORAGE_KEYS.expiredTokenTime);
        // this.#loginService.logOut()
        // this.#globalStore.removeUserDetails();
        this.navigateToLogin();
        return { ...state, token: undefined }
    })

    navigateToLogin(needToRedirect: boolean = true): string {
        var currentUrl = window.location.href; // Get the full URL
        if (currentUrl.includes("login") || currentUrl.includes("404")) {
            return '';
        }
        localStorage.removeItem(STORAGE_KEYS.userToken);
        localStorage.removeItem(STORAGE_KEYS.expiredTokenTime);
        currentUrl = currentUrl.split('?')[0];

        //cancel login option
        if (environment.auth == "idp") {
            window.location.href = `${environment.IDPAppUrlToLogin}?redirectUrl=${currentUrl}`;
            return '';
        }
        else {
            const baseUrl = `${environment.envUrl}`; // Define the part of the URL from which you want to extract
            // Check if the current URL contains '/main/' and get everything after it
            const redirectUrl = currentUrl.includes(baseUrl)
                ? currentUrl.split(baseUrl)[1]
                : undefined;
            const redirectTo = redirectUrl && !redirectUrl.includes("login") ? `?redirectUrl=${redirectUrl}` : '';
            if (needToRedirect) {
                window.location.href = `${environment.envUrl}/login${redirectTo}`;
            }
            return `/login${redirectTo}`;
        }

    }

    updateStoreVariable(key: keyof AuthState, value: any) {
        this.patchState({ [key]: value } as Partial<AuthState>);
    }
}
